import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import { getValueColumns } from 'app/util/queryResults';
import { Text } from 'core/components';
import { deviceNameRenderer, valueOrNameRenderer } from 'app/components/dataviews/views/legend/legendRenderers';
import {
  asnMetrics,
  asPathMetrics,
  findMetricOption,
  interfaceMetrics,
  ipMetrics
} from 'app/components/dataviews/views/legend/legendUtils';
import { Table, VirtualizedTable } from 'core/components/table';
import ActionsMenu from 'app/components/resultsTable/ActionsMenu';
import $dictionary from 'app/stores/$dictionary';
import { getMetricRenderer, mergeColumns } from 'app/components/resultsTable/util';
import SpinnerBody from 'core/components/table/SpinnerBody';
import Dimension from './Dimension';

/**
 * @param {import('core/form/types.d.ts').CellRenderFn} originalRenderer
 * @param {import('./types.d.ts').QueryModel} queryModel
 * @param {object} queryResultsCollection
 * @param {import('./types.d.ts').Bucket} bucket
 * @returns {import('core/form/types.d.ts').CellRenderFn}
 */
function wrapRenderer(originalRenderer, queryModel, queryResultsCollection, bucket) {
  if (!originalRenderer) {
    return ({ model, column }) => {
      if (!model || !column || !column.key) {
        return null;
      }
      return model.get(column.key);
    };
  }

  return function WrappedRenderer(propsFromTable) {
    const { model, column } = propsFromTable;

    if (!model || !column || !column.key) {
      return null;
    }

    const correctValue = model.get(column.key);

    const propsForOriginalRenderer = {
      value: correctValue,
      model,
      column,
      name: column.name || column.key,

      queryModel,
      queryResultsCollection,
      bucket
    };

    return originalRenderer(propsForOriginalRenderer);
  };
}

/**
 * @param {boolean} showDeviceForInterface
 * @param {import('./types.d.ts').Column[]} dimensionColumns
 * @returns boolean
 */
const shouldAddDeviceColumn = (showDeviceForInterface, dimensionColumns) =>
  showDeviceForInterface && dimensionColumns.some((column) => interfaceMetrics.includes(column.metric));

/**
 * @param {import('./types.d.ts').MetricsColumn[]} metricColumns
 * @param {string} metric
 * @returns {import('./types.d.ts').MetricsColumn}
 */
const getMetricColumn = (metricColumns, metric) => metricColumns.find((m) => m.metric === metric);

/**
 * @param {string[]} metrics
 * @returns {import('./types.d.ts').MetricsColumn[]}
 */
const getMetricsColumns = (metrics = []) =>
  metrics.map((metric) => {
    const metricColumnsDict = $dictionary.get('metricColumns');
    const metricOption = findMetricOption(metric);

    let minWidth = 80;
    let flexBasis = 100;
    if (
      metric.includes('name') ||
      metric.includes('description') ||
      asPathMetrics.includes(metric) ||
      interfaceMetrics.includes(metric)
    ) {
      // larger columns, should flex to take up more space if possible
      // only really tested for name/description
      minWidth = 120;
      flexBasis = 200;
    }
    if (metric === 'inet_family' || metric === 'Proto') {
      // these values are not tested
      minWidth = 60;
      flexBasis = 60;
    }
    // For metrics that are often numeric and don't need much space
    if (ipMetrics.includes(metric) || asnMetrics.includes(metric)) {
      // these values are not tested
      minWidth = 150;
      flexBasis = 120;
    }

    if (!metricOption) {
      return {
        name: metricColumnsDict[metric] || metric,
        key: metricColumnsDict[metric] || metric,
        label: metricColumnsDict[metric] || metric,
        metric,
        minWidth,
        flexBasis
      };
    }

    const { label, group, tagLabel } = metricOption;

    return {
      name: metricColumnsDict[metric] || metric,
      key: metricColumnsDict[metric] || metric,
      metric,
      label: <Dimension group={group} label={tagLabel || label} />,
      minWidth,
      flexBasis
    };
  });

/**
 * @param {import('./types.d.ts').ResultsTableProps} props
 * @returns {JSX.Element|null}
 * @private
 */
const _ResultsTable = ({
  showDeviceForInterface = true,
  showActionsMenu = false,
  showCombinedTotalColumn = false,
  showLastDatapoints = false,
  interactive = false,
  emptyState = null,
  bordered = true,
  stickyHeader = false,
  limit = undefined,
  dimensionOverrides = [],
  valueOverrides = [],
  queryResultsCollection,
  queryModel,
  bucket,
  tableClass,
  virtualized,
  onRowClick,
  ...restTableProps
}) => {
  /**
   * @type object[]
   */
  const results = useMemo(() => {
    if (!queryResultsCollection) {
      return [];
    }

    if (!queryResultsCollection.sortState.field) {
      queryResultsCollection.sort(
        /**
         * @type {string}
         */
        queryModel.get('outsort')
      );
    }

    return queryResultsCollection.slice(0, limit);
  }, [limit, queryModel, queryResultsCollection]);

  /**
   * @type import('./types.d.ts').MetricsColumn[]
   */
  const metricColumns = useMemo(() => {
    if (!queryResultsCollection) {
      return [];
    }

    return getMetricsColumns(bucket.firstQuery.get('metric'));
  }, [bucket.firstQuery, queryResultsCollection]);

  /**
   * @type import('./types.d.ts').Column[]
   */
  const dimensionColumns = useMemo(() => {
    if (!queryResultsCollection) {
      return [];
    }
    const metricColumnsDict = $dictionary.get('metricColumns');
    /**
     * @type {string[]}
     */
    const metrics = bucket.firstQuery.get('metric');
    /**
     * @type {string}
     */
    const fastData = bucket.firstQuery.get('fastData');
    /**
     * @type {boolean}
     */
    const filterDimensionsEnabled = bucket.firstQuery.get('filterDimensionsEnabled');

    /**
     * @type {import('./types.d.ts').Column[]}
     */
    let result = [];

    if (filterDimensionsEnabled) {
      const filterDimensionName = bucket.firstQuery.get('filterDimensionName');

      result.push({
        name: metricColumnsDict.Traffic,
        label: (
          <>
            <Text muted as="div" fontSize="small" fontWeight="normal">
              Filter-Based
            </Text>
            {filterDimensionName}
          </>
        ),
        renderer: valueOrNameRenderer(false),
        // these values are not tested
        minWidth: 200,
        flexBasis: 170
      });
    } else {
      metrics.forEach((metric, index) => {
        let column = getMetricColumn(metricColumns, metric);

        if (queryModel.get('aggregateFiltersEnabled')) {
          // Find the base metric information from which to derive renderers
          const [filter] = queryModel.get('aggregateFilters');
          if (filter.metric) {
            [metric] = filter.metric;
            column = getMetricColumn(metricColumns, metric);
          }

          // Update the place in which to look for key if provided
          /**
           * @type {string | null}
           */
          const aggregateFiltersDimensionLabel = queryModel.get('aggregateFiltersDimensionLabel');
          if (aggregateFiltersDimensionLabel) {
            column.key = aggregateFiltersDimensionLabel;
            column.name = aggregateFiltersDimensionLabel;
            column.label = aggregateFiltersDimensionLabel;
          }
        }

        column.renderer = getMetricRenderer({ metric, firstQuery: bucket.firstQuery, index, fastData });

        result.push(column);
      });
    }

    if (shouldAddDeviceColumn(showDeviceForInterface, result)) {
      let deviceColumn = 'i_device_id';

      if (metrics.includes('bgp_ult_exit_interface')) {
        deviceColumn = 'i_ult_exit_device_name';
      }

      result = [
        {
          ...getMetricColumn(metricColumns, deviceColumn),
          renderer: deviceNameRenderer()
        },
        ...result
      ];
    }

    return result;
  }, [bucket.firstQuery, metricColumns, queryModel, queryResultsCollection, showDeviceForInterface]);

  const combinedColumns = useMemo(() => {
    if (!queryResultsCollection) {
      return [];
    }

    const dimensionCols = mergeColumns(dimensionColumns, dimensionOverrides).filter((c) => !c.hidden);

    const valueCols = mergeColumns(
      getValueColumns({
        queryModel,
        bucket,
        showCombinedTotalColumn,
        showLastDatapoints,
        queryResultsCollection
      }),
      valueOverrides
    );

    const combined = [...dimensionCols, ...valueCols];

    if (showActionsMenu) {
      // note that `actions` prop is currently unsupported
      combined.push({
        key: '__actions__',
        width: 48,
        renderer: ({ model }) => <ActionsMenu model={model} queryModel={queryModel} />
      });
    }

    return combined.map((col) => ({
      ...col,
      key: col.key || col.name,
      renderer: wrapRenderer(col.renderer, queryModel, queryResultsCollection, bucket)
    }));
  }, [
    bucket,
    dimensionColumns,
    dimensionOverrides,
    queryModel,
    queryResultsCollection,
    showActionsMenu,
    showCombinedTotalColumn,
    showLastDatapoints,
    valueOverrides
  ]);

  if (!queryResultsCollection) {
    return <SpinnerBody />;
  }

  const tableProps = {
    ...restTableProps,
    columns: combinedColumns,
    collection: queryResultsCollection,
    stickyHeader,
    selectOnRowClick: interactive,
    onRowClick: interactive && onRowClick ? onRowClick : undefined,
    emptyStateComponent:
      results.length === 0 && queryResultsCollection.fullyLoaded && emptyState ? emptyState : undefined,
    noBorder: !bordered
  };

  return virtualized ? <VirtualizedTable {...tableProps} flexed /> : <Table {...tableProps} />;
};

/**
 * Intended as a replacement for ResultsTable, this switches the underlying implementation across to Table and also allows for virtualization with
 * VirtualizedTable. Also uses FC hooks for better render optimization. If you are creating a new ResultTable then you should use this implementation.
 * Note however that this is currently missing a small amount of functionality from ResultsTable and needs testing to ensure that column sizing is
 * consistently correct for all column types.
 *
 * Unsupported props from ResultsTableV2:
 * - `bordered` (functions differently, now defaults to true)
 * - `actions` (not implemented)
 * - `allowSelection` (not implemented)
 * - `striped` (not implemented)
 * - `condensed` (not implemented)
 * - `showSparklines` (not supported in Table/VirtualizedTable)
 * - `showRefresh` (not supported in Table/VirtualizedTable)
 * - `showLastUpdated` (not supported in Table/VirtualizedTable)
 * - `mergeOverlayCells` (not supported in Table/VirtualizedTable)
 *
 * @param {import('./types.d.ts').ResultsTableV2Props} props
 * @returns {JSX.Element|null}
 */
export const ResultsTableV2 = observer(_ResultsTable);
